import React, { useState, useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
// import { refreshAllCachedData } from '../services/api';

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // ✅ Show any flash message left behind by a forced logout / deactivation
  useEffect(() => {
    const msg = sessionStorage.getItem("flash_message");
    if (msg) {
      setError(msg);
      sessionStorage.removeItem("flash_message");
      sessionStorage.removeItem("flash_type");
    }
  }, []);

  // ✅ Handle submit with proper error prevention
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault(); // ✅ Prevents page reload
      setError("");
      setLoading(true);

      try {
        await login(email, password);

        const storedUser = sessionStorage.getItem("user");
        const loggedInUser = storedUser ? JSON.parse(storedUser) : null;

        console.log("Logged in user:", loggedInUser);
        console.log("Role:", loggedInUser?.role);

        if (loggedInUser?.role === "user") {
          navigate("/attendee-dashboard", { replace: true });
        } else if (
          loggedInUser?.role === "event_editor" ||
          loggedInUser?.role === "device_scanner"
        ) {
          navigate("/my-events", { replace: true });
        } else if (loggedInUser?.role === "vendor") {
          navigate("/vendor/overview", { replace: true });
        } else {
          navigate("/dashboard", { replace: true });
        }
      } catch (error) {
        console.log("Login error:", error);

        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Invalid email or password. Please try again.";

        setError(message);

        // Clear login fields after invalid credentials
        setEmail("");
        setPassword("");
      } finally {
        setLoading(false);
      }
    },
    [email, password, login, navigate],
  );

  // ✅ Clear error when user types
  const handleInputChange = useCallback(
    (e) => {
      if (error) setError("");
      const { name, value } = e.target;
      if (name === "email") setEmail(value);
      else if (name === "password") setPassword(value);
    },
    [error],
  );

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-xl border border-gray-100 shadow-sm p-8">
        {/* Logo */}
        <div className="text-center mb-6">
          <img
            src="https://res.cloudinary.com/bvpzaizf/image/upload/f_auto,q_auto,w_300,c_limit/v1787143638/logo2.png"
            alt="NFCfied Logo"
            className="h-14 mx-auto mb-3"
          />
          <p className="text-sm text-gray-500">Sign in to your account</p>
        </div>

        {/* ✅ Error Alert - Stays visible until user types */}
        {error && (
          <div className="mb-6 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
            <div className="flex items-start gap-2">
              <svg
                className="w-4 h-4 mt-0.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={email}
              onChange={handleInputChange}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
              placeholder="you@example.com"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              type="password"
              name="password"
              value={password}
              onChange={handleInputChange}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
              placeholder="••••••••"
              required
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#1672b9] text-white py-2.5 rounded-lg text-sm font-medium shadow-sm hover:bg-[#0f5a9e] transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Logging in...
              </>
            ) : (
              "Sign In"
            )}
          </button>
        </form>

        {/* Register Link */}
        <div className="mt-6 text-center text-sm text-gray-600">
          Don't have an account?{" "}
          <Link
            to="/register"
            className="text-[#1672b9] font-medium hover:underline"
          >
            Create account
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;